'use client';

import * as React from 'react';
import { motion } from 'framer-motion';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

// Star component for the background effect
const Star = ({ delay = 0 }: { delay?: number }) => (
  <motion.div
    className="absolute w-1 h-1 bg-white rounded-full"
    initial={{ opacity: 0, scale: 0 }}
    animate={{
      opacity: [0, 1, 0],
      scale: [0, 1, 0],
      y: [0, -20, -40],
    }}
    transition={{
      duration: 3,
      delay,
      repeat: Infinity,
      ease: 'easeOut',
    }}
    style={{
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
    }}
  />
);

// Generate multiple stars
const generateStars = (count: number) => {
  return Array.from({ length: count }, (_, i) => (
    <Star key={i} delay={i * 0.1} />
  ));
};

const heroVariants = cva(
  'relative w-screen h-screen overflow-hidden py-20 px-4 flex items-center justify-center',
  {
    variants: {
      variant: {
        cosmic: 'bg-[linear-gradient(to_right_bottom,#100c1e_0%,#2a1a5a_100%)]',
        dark: 'bg-[linear-gradient(to_right_bottom,#1a2a6c_0%,#000000_20%,#000000_80%,#1a2a6c_100%)]',
      },
    },
    defaultVariants: {
      variant: 'cosmic',
    },
  }
);
export interface HeroProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof heroVariants> {
  headline: string;
  subtext?: string;
  primaryButton?: {
    text: string;
    href?: string;
    onClick?: () => void;
    iconLeft?: React.ReactNode;
    iconRight?: React.ReactNode;
    className?: string;
  };
  secondaryButton?: {
    text: string;
    href?: string;
    onClick?: () => void;
    iconLeft?: React.ReactNode;
    iconRight?: React.ReactNode;
    className?: string;
  };
}

const SimpleHeroWithGradient = React.forwardRef<HTMLDivElement, HeroProps>(
  (
    {
      className,
      headline,
      subtext,
      primaryButton = {
        text: 'Get Started',
        href: '#',
        onClick: () => {},
        iconLeft: null,
        iconRight: (
          <svg
            className="w-4 h-4 -rotate-45"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        ),
        className: '',
      },
      secondaryButton,
      variant,
      ...props
    },
    ref
  ) => {
    const { onDrag, onDragStart, onDragEnd, ...motionProps } = props;

    return (
      <motion.div
        ref={ref}
        className={cn(heroVariants({ variant, className }))}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        // {...motionProps}
      >
        {/* Star field background */}
        <div className="absolute inset-0 overflow-hidden">
          {generateStars(50)}
        </div>

        <motion.div className="relative w-full h-fit max-w-screen-md flex flex-col items-center justify-center m-auto z-10">
          <motion.h1
            className="text-4xl md:text-5xl lg:text-6xl leading-3 font-bold text-center mb-4 text-white"
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          >
            {headline}
          </motion.h1>
          {subtext && (
            <motion.p
              className="text-sm  md:text-base text-center mb-8 opacity-80 text-white"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              {subtext}
            </motion.p>
          )}
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
            {primaryButton && (
              <motion.a
                href={primaryButton.href}
                onClick={primaryButton.onClick}
                className={cn(
                  'inline-flex items-center gap-2 bg-white text-gray-900 px-4 py-3 rounded-full font-semibold shadow-lg hover:bg-gray-100 transition-colors',
                  primaryButton.className
                )}
              >
                {primaryButton.iconLeft && (
                  <span className="flex-shrink-0 text-sm">
                    {primaryButton.iconLeft}
                  </span>
                )}
                <span className="text-sm">{primaryButton.text}</span>
                {primaryButton.iconRight && (
                  <span className="flex-shrink-0 text-sm">
                    {primaryButton.iconRight}
                  </span>
                )}
              </motion.a>
            )}

            {secondaryButton && (
              <motion.a
                href={secondaryButton.href}
                onClick={secondaryButton.onClick}
                className={cn(
                  'inline-flex items-center gap-2 border-2 border-white text-white px-4 py-3 rounded-full font-normal transition-colors hover:bg-white hover:text-gray-900',
                  secondaryButton.className
                )}
              >
                {secondaryButton.iconLeft && (
                  <span className="flex-shrink-0 text-sm">
                    {secondaryButton.iconLeft}
                  </span>
                )}
                <span className="text-sm">{secondaryButton.text}</span>
                {secondaryButton.iconRight && (
                  <span className="flex-shrink-0 text-sm">
                    {secondaryButton.iconRight}
                  </span>
                )}
              </motion.a>
            )}
          </div>
        </motion.div>
      </motion.div>
    );
  }
);
SimpleHeroWithGradient.displayName = 'Hero';

export { SimpleHeroWithGradient, heroVariants };
